import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";

type IconDropzoneProps = {
  value?: File | null;
  existingUrl?: string | null;
  onChange: (file: File | null) => void;
  error?: string;
};

const ACCEPTED = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/svg+xml", "image/webp"];

export const IconDropzone = ({
  value,
  existingUrl,
  onChange,
  error,
}: IconDropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const applyFile = useCallback(
    (file: File | null) => {
      setLocalError(null);
      if (!file) {
        onChange(null);
        return;
      }
      if (!ACCEPTED.includes(file.type)) {
        setLocalError("Use a JPEG, PNG, GIF, SVG, or WebP image.");
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setLocalError("Image must be 2MB or smaller.");
        return;
      }
      onChange(file);
    },
    [onChange],
  );

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);
    const file = event.dataTransfer.files?.[0] ?? null;
    applyFile(file);
  };

  const displaySrc = preview || existingUrl || null;
  const message = localError || error;

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragging(false);
        }}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors ${
          dragging
            ? "border-primary bg-indigo-50"
            : message
              ? "border-red-300 bg-red-50"
              : "border-gray-300 bg-gray-50 hover:border-primary"
        }`}
      >
        {displaySrc ? (
          <div className="flex flex-col items-center gap-3">
            <img
              src={displaySrc}
              alt="Context tip icon preview"
              className="h-16 w-16 object-contain rounded-lg bg-white border border-gray-200"
            />
            <p className="text-sm text-gray-600">
              Drag & drop to replace, or click to browse
            </p>
            {value && (
              <button
                type="button"
                className="text-xs text-red-500 hover:underline"
                onClick={(e) => {
                  e.stopPropagation();
                  applyFile(null);
                  if (inputRef.current) inputRef.current.value = "";
                }}
              >
                Remove selected file
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-2">
            <i className="ri-upload-cloud-2-line text-3xl text-gray-400"></i>
            <p className="text-sm font-medium text-gray-700">
              Drag & drop icon image here
            </p>
            <p className="text-xs text-gray-500">
              or click to browse (JPEG, PNG, GIF, SVG, WebP · max 2MB)
            </p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED.join(",")}
          className="hidden"
          onChange={(e) => applyFile(e.target.files?.[0] ?? null)}
        />
      </div>
      {message && <p className="text-xs text-red-500 mt-1">{message}</p>}
    </div>
  );
};
